import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiFillCloseSquare } from "react-icons/ai";
import { BsPinMap } from "react-icons/bs";
import { FaCamera, FaCheck, FaRegClone } from 'react-icons/fa';
import { FaRegCirclePause } from "react-icons/fa6";
import { GoArrowSwitch } from "react-icons/go";
import { HiMiniDocumentDuplicate } from "react-icons/hi2";
import { IoIosRemoveCircleOutline, IoIosWarning } from "react-icons/io";
import { LuCopyPlus } from "react-icons/lu";
import { MdDeleteForever, MdOutlineCancel } from "react-icons/md";
import { VscIssueReopened } from "react-icons/vsc";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Status, URLs } from '../../Constants/Consts';
import userOnlineCheck from '../../Helpers/CustomHooks/userOnlineCheck';
import { fetchApi, getRandomUuid, splitOnCapitals, generatePunchListTag } from '../../Helpers/Functions';
import { updateChangeLogs } from '../../store/ChangeLogs';
import { addProjectPunchList, deletePunchList, updatePunchlistItem } from '../../store/PunchList';
import { setModal, setModalOpen } from '../../store/Utilities';
import { RootState } from '../../store/store';
import { PunchlistApiType } from '../../types/PunchList/PunchListSchema';
import { ROLES } from '../../utils/roles';
import FormDDL from '../CoreComponents/ReactFormComponents/FormDDL';
import FormInput from '../CoreComponents/ReactFormComponents/FormInput';
import { toast } from 'react-toastify';
import ReassignDisciplineForm, { ReassignSelection, isReassignSelectionComplete } from '../../Pages/PunchList/DetailsPage/ReassignDisciplineForm';

const PunchListActionButtons = ({ item, addEditPhotoModal }: {
    item: PunchlistApiType,
    addEditPhotoModal
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const SelectedUser = useSelector((state: RootState) => state.SelectedUser.SelectedUser);
    const { RootCauses } = useSelector((state: RootState) => state.RootCauses);
    const { OnHoldCauses } = useSelector((state: RootState) => state.OnHoldCause);
    const { ProjectPunchLists } = useSelector((state: RootState) => state.PunchList);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    const SelectedUserRole = SelectedUser.role
    // console.log("SelectedUserRole", SelectedUserRole);

    const isUserInCreatorDiscipline = item.creatorDisciplineId == SelectedUser.disciplineId
    const isUserInAssignedDiscipline = item.assignedDisciplineId == SelectedUser.disciplineId
    const { statusId: status, rfrRequired, identifiedBy, drawingUuid } = item;
    const isCreator = SelectedUser.email === identifiedBy || SelectedUser.kionEmail === identifiedBy;
    // const showMarkComplete = (status === Status.Pending && rfrRequired === 0) || (status === Status.MaterialsReceived && rfrRequired === 0) || status === Status.ReadyForReInspection;
    // const showReadyForReInspection = (status === Status.Pending && rfrRequired === 1) || (status === Status.MaterialsReceived && rfrRequired === 1)
    const showMarkCompleteForAssigned = (status === Status.Pending && rfrRequired === 0 && isUserInAssignedDiscipline) || (status === Status.MaterialsReceived && rfrRequired === 0 && isUserInAssignedDiscipline)
    const showMarkComplete = showMarkCompleteForAssigned || (status === Status.ReadyForReInspection && isUserInCreatorDiscipline);
    const showReadyForReInspection = (status === Status.Pending && isUserInAssignedDiscipline && rfrRequired === 1) || (status === Status.MaterialsReceived && rfrRequired === 1 && isUserInAssignedDiscipline)
    const showReOpenDefect = (status === Status.Duplicate || status === Status.Completed || status === Status.Cancelled || status === Status.ReadyForReInspection);
    const showPlaceOnHold = status !== Status.OnHold && status !== Status.Completed && status !== Status.Cancelled;
    const showReAssignDiscipline = status !== Status.Cancelled && status !== Status.Completed;
    const showAddEditPhoto = status !== Status.Cancelled && status !== Status.Completed;
    const showMarkDuplicate = status !== Status.Duplicate && status !== Status.Completed && status !== Status.Cancelled;
    const showMarkOffHold = status === Status.OnHold;
    const showResolveConflict = status === Status.CompletedWithRootCauseConflict;
    const showMarkCancelled = status !== Status.Cancelled && status !== Status.Completed && isCreator;
    const showShowOnMap = !!drawingUuid;
    const showDeletePunchlist = SelectedUserRole === ROLES.SuperAdmin;

    const { formState: { errors }, control, register, getValues, setValue, reset } = useForm();

    const sendUpdatedItem = async (updatedItem, callback?: () => void) => {
        try {
            await fetchApi(URLs.putPunchListUrl(item.punchListUuid), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(updatedItem),
            })
                .then((res) => res.json())
                .then(() => dispatch(updatePunchlistItem(updatedItem)));
        } catch (error) {
            if (!navigator.onLine) {
                dispatch(updatePunchlistItem({
                    ...updatedItem,
                    notSync: true,
                    // Status-only actions carry no fresh modified timestamp, so the SW
                    // conflict check would false-positive against dateIdentified and park
                    // them. Skip the conflict check for these offline updates.
                    syncIgnoreConflict: true,
                    rollbackSnapshot: item.rollbackSnapshot ?? { ...item },
                }));
            } else {
                throw error;
            }
        } finally {
            callback?.()
            dispatch(setModalOpen(false));
        }
    };
    const update = React.useCallback(async (e, status) => {
        e.stopPropagation();
        const oldStatus = item.statusId;
        const updatedItem = {
            ...item,
            statusId: status,
            assignedDisciplineId: status === Status.ReadyForReInspection ? Number(item.creatorDisciplineId) : Number(item.assignedDisciplineId),
            ... (status === Status.OnHold || status === Status.MaterialsReceived ? { onHoldReasonId: selectedOnHoldCauseId.current, onHoldDetail: getValues("OnholdCauseDetails") } : {}),
        };
        if (oldStatus === Status.Completed && status === Status.Pending) {
            //TODO: if status is Pending, we need to update dateCompleted to be null (need a better place to do this)
            updatedItem.dateCompleted = null;
        }
        if (status === Status.MaterialsReceived) {
            // Removing from hold resets the due date to 48 hours (2 days) from today.
            const offHoldDueDate = new Date();
            offHoldDueDate.setDate(offHoldDueDate.getDate() + 2);
            updatedItem.dueDate = offHoldDueDate.toISOString().split('T')[0];
        }
        await sendUpdatedItem(updatedItem);
    }, [item]);

    const handleSubmitPLOnHold = async (e, status) => {
        e.stopPropagation();
        if (!selectedOnHoldCauseId.current) return;
        const updatedItem = {
            ...item,
            statusId: status,
            assignedDisciplineId: status === Status.ReadyForReInspection ? Number(item.creatorDisciplineId) : Number(item.assignedDisciplineId),
            onHoldReasonId: selectedOnHoldCauseId.current, onHoldDetail: getValues("onHoldDetail"),
        }

        const callback = () => {
            selectedOnHoldCauseId.current = undefined;
            reset()
        }

        await sendUpdatedItem(updatedItem, callback);
        try {
            if (navigator.onLine) {
                await fetchChangeLogs();
            } else {
                // console.log("User is offline, not fetching new changelogs.");
            }
        } catch (error) {
            console.error('Error updating or fetching change logs:', error);
        }

    }

    const selectedAssignedRootCauseId = useRef(RootCauses?.find(rootCause => item.assignedRootCauseId === rootCause.rootCauseId)?.rootCauseId)
    const selectedCreatorRootCauseId = useRef(RootCauses?.find(rootCause => item.creatorRootCauseId === rootCause.rootCauseId)?.rootCauseId)
    const selectedOnHoldCauseId = useRef(OnHoldCauses?.find(rootCause => item.creatorRootCauseId === rootCause.ohrId)?.ohrId)
    const reassignSelectionRef = useRef<ReassignSelection>({
        disciplineId: item.assignedDisciplineId ?? null,
        technologyId: item.technologyId ?? null,
        categoryId: item.categoryId ?? null,
        issueId: item.issueId ?? null,
    })
    // Set by ReassignDisciplineForm on mount; lets us flip the form into
    // "show errors" mode after a failed submit attempt.
    const triggerReassignValidationRef = useRef<(() => void) | null>(null)

    const reAssign = async () => {
        const selection = reassignSelectionRef.current;
        if (!isReassignSelectionComplete(selection)) {
            triggerReassignValidationRef.current?.();
            toast.warn('Please fill in Discipline, Technology, Category, and Issue before saving.');
            // Return false to keep the modal open so the user can fix the empty fields.
            return false as const;
        }
        const updatedItem = {
            ...item,
            assignedDisciplineId: selection.disciplineId,
            technologyId: selection.technologyId,
            categoryId: selection.categoryId,
            issueId: selection.issueId,
        };
        let saveSucceeded = false;
        try {
            await fetchApi(URLs.putPunchListUrl(item.punchListUuid), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(updatedItem),
            })
                .then((res) => res.json())
                .then(() =>
                    dispatch(
                        updatePunchlistItem(updatedItem)
                    )
                );
            saveSucceeded = true;
        } catch (error) {
            if (!navigator.onLine) {
                dispatch(
                    updatePunchlistItem({
                        ...updatedItem,
                        notSync: true,
                        syncIgnoreConflict: true,
                        rollbackSnapshot: item.rollbackSnapshot ?? { ...item },
                    })
                );
            } else {
                console.error('Reassign failed:', error);
                toast.error('Failed to reassign. Please try again.');
                // Keep modal open so the user can retry without re-entering their selection.
                return false as const;
            }
        }

        if (saveSucceeded && navigator.onLine) {
            await fetchChangeLogs();
        }
    }


    const handleSubmitStatusWithRootCause = async (status: number, _: boolean, isResolveConflict?: boolean, isCreatorStep?: boolean) => {
        if (!selectedAssignedRootCauseId.current) return

        // const selectedRootCauseId = selectedAssignedRootCauseId.current
        let updatedItem: PunchlistApiType = null;
        // const descriptionnKey = isCurrentUserAssingee ? "assignedRootCauseText" : "creatorRootCauseText";
        // const rootCauseKey = isCurrentUserAssingee ? "assignedRootCause" : "creatorRootCause";
        let dateCompletedValue = null;
        if (status === Status.Completed || status === Status.CompletedWithRootCauseConflict) {
            dateCompletedValue = new Date().toISOString();
        }
        const colnedPLItem = { ...item, dateCompleted: dateCompletedValue }
        const assignedDescription = getValues("assignedRootCauseText");
        const assignedRootCause = getValues("assignedRootCause");
        const creatorDescription = getValues("creatorRootCauseText");
        const creatorRootCause = getValues("creatorRootCause");

        switch (true) {
            case isResolveConflict:
                // const rootCauseKey = isCurrentUserAssingee ? "assignedRootCauseId" : "creatorRootCauseId"
                // const disabledCauseKey = isCurrentUserAssingee ? "creatorRootCauseId" : "assignedRootCauseId"
                // const currentStatus = item[disabledCauseKey] === assignedRootCause ? status : Status.CompletedWithRootCauseConflict
                const currentStatus = creatorRootCause === assignedRootCause ? status : Status.CompletedWithRootCauseConflict
                updatedItem = {
                    ...colnedPLItem,
                    statusId: currentStatus,
                    assignedRootCauseText: assignedDescription,
                    assignedRootCauseId: assignedRootCause,
                    creatorRootCauseText: creatorDescription,
                    creatorRootCauseId: creatorRootCause,
                };
                break;

            case !rfrRequired:
                // assigned and creator id will be same
                updatedItem = {
                    ...colnedPLItem,
                    statusId: status,
                    assignedRootCauseId: selectedAssignedRootCauseId.current,
                    creatorRootCauseId: selectedAssignedRootCauseId.current,
                    creatorRootCauseText: assignedDescription,
                    assignedRootCauseText: assignedDescription
                };
                break;

            case isCreatorStep || (rfrRequired && item.assignedRootCauseId && !item.creatorRootCauseId):
                const isFinalCauseIdEqualResolverCauseId = item.assignedRootCauseId === selectedCreatorRootCauseId.current;
                const newStatus = isFinalCauseIdEqualResolverCauseId ? status : Status.CompletedWithRootCauseConflict;
                updatedItem = { ...colnedPLItem, statusId: newStatus, creatorRootCauseId: selectedCreatorRootCauseId.current, creatorRootCauseText: assignedDescription };
                break;

            default:
                updatedItem = { ...colnedPLItem, statusId: status, assignedRootCauseId: selectedAssignedRootCauseId.current, assignedRootCauseText: assignedDescription };
        }

        try {
            await fetchApi(URLs.putPunchListUrl(item.punchListUuid), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(updatedItem),
            })
                .then((res) => res.json())
                .then(() => dispatch(updatePunchlistItem(updatedItem)));
        } catch (error) {
            if (!navigator.onLine) {
                dispatch(updatePunchlistItem({
                    ...updatedItem,
                    notSync: true,
                    syncIgnoreConflict: true,
                    rollbackSnapshot: item.rollbackSnapshot ?? { ...item },
                }));
            } else {
                console.error('Status update failed:', error);
                toast.error('Failed to update status. Please try again.');
                throw error;
            }
        } finally {
            dispatch(setModalOpen(false))
            selectedAssignedRootCauseId.current = undefined
            selectedCreatorRootCauseId.current = undefined
        }
    }

    const { allDisciplines: disciplines } = useSelector((state: RootState) => state.Discipline)
    const { RootCauses: rootCausesOptions } = useSelector((state: RootState) => state.RootCauses)

    const isCurrentUserAssigned = item.assignedDisciplineId === SelectedUser.disciplineId
    const isCurrentUserCreator = item.creatorDisciplineId === SelectedUser.disciplineId

    const handlePLActionButtonClick = (props) => {

        const { e, message, status, isResolveConflict, isAssignedStep, isCreatorStep, isReopen } = props
        e.stopPropagation();

        if (isReopen) return updateModal(e, message, status)

        const isCurrentUserAssingee = isAssignedStep ?? item.assignedRootCauseId === null ? true : false

        const AssignedData = {
            id: +item.assignedDisciplineId, rootCause: +item.assignedRootCauseId, description: item.assignedRootCauseText, label: "Assigned Discipline Root Cause",
            selectInputKey: "assignedRootCause", inputKey: "assignedRootCauseText", detailsLabel: "AssignedDisciplineRootCauseDetails", testId: "assigned-discipline-root-cause-details"
        }

        const CreatorData = {
            id: +item.creatorDisciplineId, rootCause: +item.creatorRootCauseId, description: item.creatorRootCauseText, label: "Creator Discipline Root Cause",
            selectInputKey: "creatorRootCause", inputKey: "creatorRootCauseText", detailsLabel: "CreatorDisciplineRootCauseDetails", testId: "creator-discipline-root-cause-details"
        }

        // const activeUser = isCreatorStep ? CreatorData : AssignedData
        // const disabledUser = isCreatorStep ? AssignedData : CreatorData

        // watch(activeUser.inputKey)
        setValue(CreatorData.selectInputKey, rootCausesOptions?.find(item => item.rootCauseId === CreatorData.rootCause)?.rootCauseId)
        setValue(AssignedData.selectInputKey, rootCausesOptions?.find(item => item.rootCauseId === AssignedData.rootCause)?.rootCauseId)

        // if (isAssignedStep || isCreatorStep) selectedAssignedRootCauseId.current = activeUser.rootCause ?? undefined

        selectedAssignedRootCauseId.current = AssignedData.rootCause ?? undefined
        selectedCreatorRootCauseId.current = CreatorData.rootCause ?? undefined
        const getCurrentDescriptionProps = (currentObject: typeof AssignedData) => ({
            inputKey: currentObject.inputKey, registeredProps: register(currentObject.inputKey), placeholder: "", dataTestId: `${currentObject.testId}-input`,
            label: splitOnCapitals(currentObject.detailsLabel), defaultValue: item[currentObject.inputKey] || getValues(currentObject.inputKey)
        })

        const getCurrentSelectInputProps = (currentObject: typeof AssignedData) => ({
            key: currentObject.selectInputKey,
            inputKey: currentObject.selectInputKey,
            name: currentObject.selectInputKey,
            label: currentObject.label,
            dataTestId: `${currentObject.testId}-dropdown-input`,
            defaultValue: rootCausesOptions.find(item => item.rootCauseId === currentObject.rootCause),
        })

        const activeDDOptions = rootCausesOptions?.map(rootCause => ({ text: rootCause.rootCauseDescription, value: rootCause.rootCauseId }))
        dispatch(setModal({
            Chilren: () =>
                <>
                    <FormDDL
                        options={activeDDOptions}
                        control={control}
                        errors={errors}
                        onChangeEvent={selectedValue => selectedAssignedRootCauseId.current = Number(selectedValue ?? '')}
                        {...getCurrentSelectInputProps(AssignedData)} disabled={(isCreatorStep || isResolveConflict) && !isUserInAssignedDiscipline} />
                    <FormInput errors={errors} {...getCurrentDescriptionProps(AssignedData)} disabled={(isCreatorStep || isResolveConflict) && !isUserInAssignedDiscipline}/>
                    {!showMarkCompleteForAssigned && <>
                        <FormDDL
                            options={rootCausesOptions?.map(rootCause => ({ text: rootCause.rootCauseDescription, value: rootCause.rootCauseId }))}
                            control={control}
                            errors={errors}
                            onChangeEvent={selectedValue => selectedCreatorRootCauseId.current = Number(selectedValue ?? '')}
                            {...getCurrentSelectInputProps(CreatorData)} disabled={!isCreatorStep && !isResolveConflict} />
                        <FormInput errors={errors} {...getCurrentDescriptionProps(CreatorData)} disabled={!isCreatorStep && !isResolveConflict} />
                    </>}
                    <h3>{message}</h3>
                </>,
            onConfirm: () => handleSubmitStatusWithRootCause(status, isCurrentUserAssingee, isResolveConflict, isCreatorStep),
            open: true,
        }))
    };


    const reAssignModal = () => {
        setIsMobileMenuOpen(false);

        // Reset selection to current item values each time the modal opens so that
        // dismissing without saving and re-opening starts from the persisted state.
        const initial: ReassignSelection = {
            disciplineId: item.assignedDisciplineId ?? null,
            technologyId: item.technologyId ?? null,
            categoryId: item.categoryId ?? null,
            issueId: item.issueId ?? null,
        };
        reassignSelectionRef.current = initial;

        const disciplineOptions = (disciplines ?? []).map(d => ({
            text: String(d.disciplineName),
            value: d.disciplineId,
        }));

        dispatch(setModal({
            Chilren: () => (
                <ReassignDisciplineForm
                    initial={initial}
                    disciplineOptions={disciplineOptions}
                    onChange={(s) => { reassignSelectionRef.current = s; }}
                    triggerValidationRef={triggerReassignValidationRef}
                />
            ),
            onConfirm: () => reAssign(),
            open: true,
        }))
    };

    const handleClonePunchlist = async () => {
        const randomPunchListId = getRandomUuid();

        // Generate a new punch list tag for the cloned item using user's idTagInitial
        const punchListTag = generatePunchListTag(
            SelectedUser.idTagInitial || '',
            ProjectPunchLists
        );

        const clonedItem = {
            ...item,
            punchListUuid: randomPunchListId,
            punchListTag: punchListTag
        }
        try {
            await fetchApi(URLs.postPunchListsUrl(), {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(clonedItem)
            }).then(res => res.json()).then((list) => {
                dispatch(addProjectPunchList({ ...clonedItem, punchListUuid: list.itemId }))
            })
        } catch (error) {
            if (!navigator.onLine) {
                dispatch(addProjectPunchList({ ...clonedItem, notSync: true, creation: true, }))
            }
        }
        toast.success("Cloned Punchlist created successfully")
        navigate(`/PunchList/DetailsPage/${randomPunchListId}`, { replace: true })
    }

    useEffect(() => {
        return () => {
            dispatch(setModalOpen(false))
        }
    }, [])

    const online = userOnlineCheck();

    const fetchChangeLogs = async () => {
        if (online) {
            try {
                const endpoint = URLs.getUpdatedChangeLogsUrl(item.projectNumber, null, null, item.punchListUuid);
                const logs = await fetchApi(endpoint)
                    .then(res => res.json());
                if (logs.status != 400) dispatch(updateChangeLogs({ uuidType: "Punchlist", uuid: item.punchListUuid, data: logs }));
            } catch (error) {
                console.error('Failed to fetch changelogs:', error);
            }
        }
    };

    const handleOnHoldAction = (e, message, status) => {
        const onHoldOptions = OnHoldCauses?.map(rootCause => ({ text: rootCause.ohrDescription, value: rootCause.ohrId }));
        setIsMobileMenuOpen(false);
        dispatch(setModal({
            Chilren: () =>
                <>
                    <FormDDL
                        options={onHoldOptions}
                        control={control}
                        errors={errors}
                        onChangeEvent={selectedValue => selectedOnHoldCauseId.current = Number(selectedValue ?? '')}
                        inputKey="OnholdCauseCause"
                        name='OnholdCauseCause'
                        key='OnholdCauseCause'
                        label='On Hold Cause'
                        dataTestId="on-hold-cause-dropdown-input"
                    />
                    <FormInput
                        errors={errors}
                        inputKey="onHoldDetail"
                        key="onHoldDetail"
                        registeredProps={register("onHoldDetail")}
                        placeholder="please enter the cause details"
                        label={splitOnCapitals("OnHoldCauseDetails")}
                        defaultValue={getValues("onHoldDetail") ?? ""}
                        dataTestId="on-hold-cause-details-input"

                    />
                    <h3>{message}</h3>
                </>,
            onConfirm: async () => await handleSubmitPLOnHold(e, status)
            ,
            open: true
        }));
    }
    const updateModal = (e, message, status) => {
        setIsMobileMenuOpen(false);
        dispatch(setModal({
            Chilren: () => <>{message}</>,
            onConfirm: async () => {
                try {
                    await update(e, status);
                    if (navigator.onLine) {
                        await fetchChangeLogs();
                    } else {
                        // console.log("User is offline, not fetching new changelogs.");
                    }
                } catch (error) {
                    console.error('Error updating or fetching change logs:', error);
                }
            },
            open: true
        }));
    };
    const handleDeletePunchList = async (uuid) => {
        navigate(-1);
        if (navigator.onLine) {
            await fetchApi(URLs.deletePunchListUrl(uuid), {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json'
                }
            }).then(() => {
                dispatch(deletePunchList({ uuid, notSync: false }));
            });
        } else {
            // Handle offline case
            dispatch(deletePunchList({ uuid, notSync: true }));
        }
    }

    const deletePunchlistModal = () => {
        setIsMobileMenuOpen(false);
        dispatch(setModal({
            Chilren: () => <>{"Are you sure you want to delete this punchlist?"}</>,
            onConfirm: async () => {
                try {
                    await handleDeletePunchList(item.punchListUuid);
                    if (navigator.onLine) {
                        await fetchChangeLogs();
                    } else {
                        // console.log("User is offline, not fetching new changelogs.");
                    }
                } catch (error) {
                    console.error('Error updating or fetching change logs:', error);
                }
            },
            open: true
        }));
    }

    const CloneButton = <div className="flex flex-col items-center">
        <div
            onClick={handleClonePunchlist}
            data-testid="clone-punchlist-button"
            className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000">
            <FaRegClone className="text-white" />
        </div>
        <div className="text-center text-xs font-bold mt-1 uppercase">
            <p>Clone</p>
            <p>Punch List </p>
        </div>
    </div>

    return (
        <>

            {/* Mobile Action Buttons Menu */}

            <div onClick={toggleMobileMenu} className='flex items-center fixed bottom-5 right-5 z-50 bg-orange-1000 p-2 rounded-xl shadow-lg md:hidden'>
                {isMobileMenuOpen ? (
                    <>
                        <span className='text-xs text-white mr-2'>Close:</span>
                        <AiFillCloseSquare className="text-white" />
                    </>
                ) : (
                    <>
                        <span className='text-xs text-white mr-2'>Actions:</span>
                        <LuCopyPlus className="text-white" />
                    </>
                )}
            </div>

            <div className={`${isMobileMenuOpen ? 'flex' : 'hidden'} justify-center fixed inset-0 z-40 bg-white dark:bg-black p-5 md:hidden mt-6`}>
                <div className="w-full max-w-xs mx-auto">
                    {showReOpenDefect && (
                        <div className="flex items-center mt-4" data-testid="web-re-open-punchlist-button" onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to Re-Open Defect?", status: Status.Pending, isReopen: true, isAssignedStep: true })}>
                            <div className="rounded-full bg-[#fdb419] p-2 cursor-pointer"><VscIssueReopened className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Re-Open Defect</p></div>
                        </div>
                    )}
                    {showMarkComplete && (
                        <div className="flex items-center mt-4" data-testid="web-mark-complete-punchlist-button" onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to mark this Complete?", status: Status.Completed, isCreatorStep: true })}>
                            <div className="rounded-full bg-green-500 p-2 cursor-pointer"><FaCheck className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Mark Completed</p></div>
                        </div>
                    )}
                    {showResolveConflict && (
                        <div className="flex items-center mt-4" data-testid="web-resolve-conflict-punchlist-button" onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to mark this Complete?", status: Status.Completed, isResolveConflict: true, isCreator: isCurrentUserCreator, isAssignedStep: isCurrentUserAssigned })}>
                            <div className="rounded-full bg-red-500 p-2 cursor-pointer"><IoIosWarning className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Resolve Conflict</p></div>
                        </div>
                    )}
                    {showReadyForReInspection && (
                        <div className="flex items-center mt-4" data-testid="web-ready-for-reInspection-punchlist-button" onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to mark this Ready For Re-Inspection?", status: Status.ReadyForReInspection, isAssignedStep: true })}>
                            <div className="rounded-full bg-green-500 p-2 cursor-pointer"><FaCheck className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Ready For Re-Inspection</p></div>
                        </div>
                    )}
                    {showPlaceOnHold && (
                        <div className="flex items-center mt-4" data-testid="web-place-on-hold-punchlist-button" onClick={e => handleOnHoldAction(e, "Are you sure you want to place this On Hold?", Status.OnHold)}>
                            <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-gray-700"><FaRegCirclePause className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Place On Hold</p></div>
                        </div>
                    )}
                    {showMarkOffHold && (
                        <div className="flex items-center mt-4" data-testid="web-remove-hold-punchlist-button" onClick={e => updateModal(e, "Are you sure you want to place this Off-Hold?", Status.MaterialsReceived)}>
                            <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-gray-700"><IoIosRemoveCircleOutline className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>REMOVE HOLD</p></div>
                        </div>
                    )}
                    {showReOpenDefect && (
                        <div className="flex items-center mt-4" data-testid="web-re-open-punchlist-button" onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to Re-Open Defect?", status: Status.Pending, isReopen: true, isAssignedStep: true })}>
                            <div className="rounded-full bg-[#fdb419] p-2 flex justify-center items-center cursor-pointer"><VscIssueReopened className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>REMOVE HOLD</p></div>
                        </div>
                    )}

                    {showAddEditPhoto && (
                        <div className="flex items-center mt-4" data-testid="web-add-edit-photo-punchlist-button" onClick={() => addEditPhotoModal()}>
                            <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"><FaCamera className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>ADD/EDIT PHOTO</p></div>
                        </div>
                    )}
                    {showReAssignDiscipline && (
                        <div className="flex items-center mt-4" data-testid="web-reAssign-discipline-punchlist-button" onClick={reAssignModal}>
                            <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"><GoArrowSwitch className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Re-Assign Discipline</p></div>
                        </div>
                    )}
                    {showMarkDuplicate && (
                        <div className="flex items-center mt-4" data-testid="web-mark-duplicate-punchlist-button" onClick={e => updateModal(e, "Are you sure you want to mark this as Duplicate?", Status.Duplicate)}>
                            <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"><HiMiniDocumentDuplicate className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Mark Duplicate</p></div>
                        </div>
                    )}
                    {showMarkCancelled && (
                        <div className="flex items-center mt-4" data-testid="web-mark-cancelled-punchlist-button" onClick={e => updateModal(e, "Are you sure you want to mark this Cancelled?", Status.Cancelled)}>
                            <div className="rounded-full bg-red-500 p-2 flex justify-center items-center cursor-pointer"><MdOutlineCancel className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Mark Cancelled</p></div>
                        </div>
                    )}
                    {showShowOnMap && (
                        <div className="flex items-center mt-4" data-testid="web-show-on-map-punchlist-button" onClick={() => { navigate(`/DrawingLayouts/MapView/${item.drawingUuid}/${item.punchListUuid}`) }}>
                            <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"><BsPinMap className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Show On Map</p></div>
                        </div>
                    )}
                    {CloneButton}
                    {showDeletePunchlist && (
                        <div className="flex items-center mt-4" data-testid="web-delete-punchlist-button" onClick={deletePunchlistModal}>
                            <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"><MdDeleteForever className="text-white" /></div>
                            <div className="text-xs font-bold uppercase ml-4"><p>Delete</p></div>
                        </div>
                    )}
                </div>
            </div>

            {/* Top Action Buttons */}

            <div className="justify-start pt-8 mb-8 gap-4 hidden md:inline-flex  ml-10">
                {showMarkComplete && (
                    <div className="flex flex-col items-center">
                        <div data-testid="mobile-mark-complete-punchlist-button"
                            onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to mark this Complete?", status: Status.Completed, isCreatorStep: true })}
                            className="rounded-full bg-green-500 p-2 flex justify-center items-center cursor-pointer">
                            <FaCheck className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Mark</p>
                            <p>Completed</p>
                        </div>
                    </div>
                )}
                {showResolveConflict && (
                    <div className="flex flex-col items-center">
                        <div data-testid="mobile-resolve-conflict-punchlist-button"
                            onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to mark this Complete?", status: Status.Completed, isResolveConflict: true, isCreator: isCurrentUserCreator, isAssignedStep: isCurrentUserAssigned })}
                            className="rounded-full  bg-red-500  p-2 flex justify-center items-center cursor-pointer">
                            <IoIosWarning className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Resolve</p>
                            <p>Conflict</p>
                        </div>
                    </div>
                )}
                {showReadyForReInspection && (
                    <div className="flex flex-col items-center">
                        <div
                            data-testid="mobile-ready-for-reInspection-punchlist-button"
                            onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to mark this Ready For Re-Inspection?", status: Status.ReadyForReInspection, isAssignedStep: true })}
                            className="rounded-full bg-green-500 p-2 flex justify-center items-center cursor-pointer">
                            <FaCheck className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Ready For</p>
                            <p>Re-Inspection</p>
                        </div>
                    </div>
                )}
                {showPlaceOnHold && (
                    <div className="flex flex-col items-center">
                        <div
                            data-testid="mobile-place-on-hold-punchlist-button"
                            onClick={e => handleOnHoldAction(e, "Are you sure you want to place this On Hold?", Status.OnHold)}
                            className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-gray-700">
                            <FaRegCirclePause className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Place On</p>
                            <p>Hold</p>
                        </div>
                    </div>
                )}

                {showMarkOffHold && (
                    <div className="flex flex-col items-center">
                        <div
                            data-testid="mobile-remove-hold-punchlist-button"
                            onClick={e => updateModal(e, "Are you sure you want to remove this item from the on hold status?", Status.MaterialsReceived)}
                            className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-gray-700">
                            <IoIosRemoveCircleOutline className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>REMOVE</p>
                            <p>HOLD</p>
                        </div>
                    </div>
                )}
                {showReOpenDefect && (
                    <div className="flex flex-col items-center">
                        <div
                            data-testid="mobile-re-open-punchlist-button"
                            onClick={e => handlePLActionButtonClick({ e, message: "Are you sure you want to Re-Open Defect?", status: Status.Pending, isReopen: true, isAssignedStep: true })}
                            className="rounded-full bg-[#fdb419] p-2 flex justify-center items-center cursor-pointer">
                            <VscIssueReopened className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Re-Open</p>
                            <p>Defect</p>
                        </div>
                    </div>
                )}
                {showAddEditPhoto && (
                    <div className="flex flex-col items-center">
                        <div className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"
                            onClick={() => addEditPhotoModal()}
                            data-testid="mobile-add-edit-photo-punchlist-button"
                        >
                            <FaCamera className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>ADD/EDIT</p>
                            <p>PHOTO</p>
                        </div>
                    </div>
                )}
                {showReAssignDiscipline && (
                    <div className="flex flex-col items-center">
                        <div
                            onClick={reAssignModal}
                            className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"
                            data-testid="mobile-reAssign-discipline-punchlist-button"
                        >
                            <GoArrowSwitch className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Re-Assign</p>
                            <p>Discipline</p>
                        </div>
                    </div>
                )}
                {showMarkDuplicate && (
                    <div className="flex flex-col items-center">
                        <div
                            onClick={e => updateModal(e, "Are you sure you want to mark this as Duplicate?", Status.Duplicate)}
                            className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000"
                            data-testid="mobile-mark-duplicate-punchlist-button"
                        >
                            <HiMiniDocumentDuplicate className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Mark</p>
                            <p>Duplicate</p>
                        </div>
                    </div>
                )}
                {showMarkCancelled && (
                    <div className="flex flex-col items-center">
                        <div
                            onClick={e => updateModal(e, "Are you sure you want to mark this Cancelled?", Status.Cancelled)}
                            data-testid="mobile-mark-cancelled-punchlist-button"
                            className="rounded-full bg-red-500 p-2 flex justify-center items-center cursor-pointer">
                            <MdOutlineCancel className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Mark</p>
                            <p>Cancelled</p>
                        </div>
                    </div>
                )}
                {showShowOnMap && (
                    <div className="flex flex-col items-center">
                        <div
                            onClick={() => {
                                navigate(`/DrawingLayouts/MapView/${item.drawingUuid}/${item.punchListUuid}`)
                            }}
                            data-testid="mobile-show-on-map-punchlist-button"
                            className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000">
                            <BsPinMap className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Show</p>
                            <p>On Map</p>
                        </div>
                    </div>
                )}
                {CloneButton}
                {showDeletePunchlist && (
                    <div className="flex flex-col items-center">
                        <div
                            onClick={deletePunchlistModal}
                            data-testid="mobile-delete-punchlist-button"
                            className="rounded-full p-2 flex justify-center items-center cursor-pointer bg-orange-1000">
                            <MdDeleteForever className="text-white" />
                        </div>
                        <div className="text-center text-xs font-bold mt-1 uppercase">
                            <p>Delete</p>
                            <p>Punch List</p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default PunchListActionButtons;

